/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, LinearProgress } from '@mui/material';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  GEOWEB_ROLE_PRESETS_ADMIN,
  useAuthenticationContext,
} from '@opengeoweb/authentication';
import {
  AlertBanner,
  Filter,
  LastUpdateTime,
  SearchField,
} from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { routerActions } from '@opengeoweb/store';
import WorkspaceMenuLabelsPanel from './WorkspaceMenuLabelsWrapper';
import WorkspacePresetInfo from './WorkspacePresetInfo';
import WorkspaceSelectList from './WorkspaceSelectList';
import { getWorkspace, updateWorkspace } from '../../api/workspace-api/api';
import { useWorkspacePresets } from '../../api/workspace-api/hooks';
import type { AppStore } from '../../store/types';
import { workspaceActions } from '../../store/workspace/reducer';
import { getWorkspaceState } from '../../store/workspace/selectors';
import type {
  WorkspacePresetForBE,
  WorkspacePresetLabels,
  WorkspacePresetListItem,
  WorkspaceViewsFromBE,
} from '../../store/workspace/types';
import { WorkspacePresetAction } from '../../store/workspace/types';
import { workspaceListSelectors } from '../../store/workspaceList';
import { workspaceListActions } from '../../store/workspaceList/reducer';
import type {
  PresetFilterType,
  WorkspaceActionDialogType,
  WorkspacePresetFormValues,
} from '../../store/workspaceList/types';
import {
  WorkspaceListErrorCategory,
  WorkspaceListErrorType,
} from '../../store/workspaceList/types';
import { getEmptyMapWorkspace } from '../../store/workspaceList/utils';
import {
  expandWorkspaceViewPresets,
  sanitizeWorkspaceForClipboard,
  writeToClipboard,
} from '../../utils/clipboardUtils';
import { useWorkspaceTranslation } from '../../utils/i18n';
import { getWorkspaceRouteUrl } from '../../utils/routes';
import { filterAndSortPresets } from '../../utils/utils';

interface WorkspaceSelectListConnectProps {
  isLabelFilterOpen?: boolean;
  onToggleLabelFilter?: () => void;
}

export const WorkspaceSelectListConnect: React.FC<
  WorkspaceSelectListConnectProps
> = ({
  isLabelFilterOpen = false,
  onToggleLabelFilter,
}: WorkspaceSelectListConnectProps) => {
  const dispatch = useDispatch();
  const { t } = useWorkspaceTranslation();
  const { currentRole, isLoggedIn, auth } = useAuthenticationContext();
  const username = auth?.username || '';
  const isAdmin = currentRole?.name === GEOWEB_ROLE_PRESETS_ADMIN.name;
  const scope = !isAdmin ? 'user' : 'system';

  const workspaceSearchQuery = useSelector((store: AppStore) =>
    workspaceListSelectors.getWorkspaceListSearchQuery(store),
  );

  const {
    data: workspacePresets = [],
    isFetching,
    error: queryError,
    refetch: refreshWorkspacePresets,
  } = useWorkspacePresets();

  const selectedFilters = useSelector(
    workspaceListSelectors.getWorkspaceListFilters,
  );
  const labelFilters = useSelector((store: AppStore) =>
    workspaceListSelectors.getWorkspaceLabelFilters(store),
  );

  const hasActiveLabelFilters = React.useMemo(() => {
    if (!labelFilters) {
      return false;
    }

    return Object.values(labelFilters).some(
      (group) => Array.isArray(group) && group.length > 0,
    );
  }, [labelFilters]);

  const activeLabelFilterCount = React.useMemo(() => {
    if (!labelFilters) {
      return 0;
    }

    return Object.values(labelFilters).flat().length;
  }, [labelFilters]);

  const filteredPresets = React.useMemo(
    () =>
      filterAndSortPresets(
        workspacePresets,
        selectedFilters,
        username,
        isAdmin,
        labelFilters ?? undefined,
        workspaceSearchQuery,
      ),
    [
      workspacePresets,
      selectedFilters,
      username,
      isAdmin,
      labelFilters,
      workspaceSearchQuery,
    ],
  );

  const infoPanelAnchorRef = React.useRef<HTMLDivElement | null>(null);

  const [infoPreset, setInfoPreset] = React.useState<WorkspacePresetListItem>();

  const labelsFormMethods = useForm<WorkspacePresetFormValues>({
    defaultValues: {
      labels: labelFilters,
    },
  });

  const currentView = useSelector((store: AppStore) =>
    getWorkspaceState(store),
  );

  const workspaceListFilters = useSelector((store: AppStore) =>
    workspaceListSelectors.getWorkspaceListFilters(store, scope, isAdmin),
  );

  const error =
    useSelector((store: AppStore) =>
      workspaceListSelectors.getWorkspaceListError(store),
    ) ??
    (queryError
      ? {
          type: WorkspaceListErrorType.VERBATIM_ERROR as const,
          category:
            queryError.cause === 401 || queryError.cause === 403
              ? WorkspaceListErrorCategory.AUTH
              : WorkspaceListErrorCategory.GENERIC,
          message: queryError.message,
        }
      : undefined);

  const onLabelsFilterChange = React.useCallback(
    (labels: WorkspacePresetLabels) => {
      dispatch(
        workspaceListActions.setWorkspaceLabelFilters({
          labels,
        }),
      );
    },
    [dispatch],
  );

  const showLabelFilterToggle = Boolean(onToggleLabelFilter);

  const fetchWorkspace = React.useCallback(
    (workspaceId: string) => {
      dispatch(workspaceActions.fetchWorkspace({ workspaceId }));
    },
    [dispatch],
  );

  const setWorkspaceActionDialogOptions = React.useCallback(
    (dialogOptions: WorkspaceActionDialogType) => {
      dispatch(
        workspaceListActions.openWorkspaceActionDialogOptions(dialogOptions),
      );
    },
    [dispatch],
  );

  const toggleSelectFilterChip = React.useCallback(
    (id: PresetFilterType, isSelected: boolean) => {
      dispatch(
        workspaceListActions.toggleSelectFilterChip({
          id,
          isSelected,
          isAdmin,
        }),
      );
    },
    [dispatch, isAdmin],
  );

  const setSelectAllFilterChip = React.useCallback(() => {
    dispatch(workspaceListActions.setSelectAllFilterChip());
  }, [dispatch]);

  const searchFilter = React.useCallback(
    (searchQuery: string) => {
      dispatch(workspaceListActions.searchFilter({ searchQuery }));
    },
    [dispatch],
  );

  // If we're loading the `New workspace` preset as defined in the FE, use this instead of retrieving from BE
  const onClickNewPreset = React.useCallback((): void => {
    dispatch(routerActions.navigateToUrl({ url: getWorkspaceRouteUrl() }));
    dispatch(
      workspaceListActions.toggleWorkspaceDialog({
        isWorkspaceListDialogOpen: false,
      }),
    );
    dispatch(
      workspaceActions.changeToEmptyMapWorkspace({
        newMapPresetText: t('workspace-mappreset-new'),
        newWorkspaceText: t('workspace-new'),
      }),
    );
  }, [dispatch, t]);

  const onClickWorkspacePreset = (id: string): void => {
    if (id === getEmptyMapWorkspace(t).id) {
      onClickNewPreset();
    } else if (!isFetching) {
      fetchWorkspace(id);
    }
  };

  const onClickFilterChip = (id: string, shouldSelect: boolean): void => {
    if (id === 'all') {
      if (shouldSelect) {
        setSelectAllFilterChip();
      }
    } else {
      toggleSelectFilterChip(id as PresetFilterType, shouldSelect);
    }
  };

  const currentViewsToBE = React.useCallback((): WorkspaceViewsFromBE[] => {
    const ws = currentView;
    const result: WorkspaceViewsFromBE[] = [];
    (ws?.views?.allIds ?? []).forEach((id: string) => {
      const view = ws.views.byId[id] ?? {};
      result.push({
        mosaicNodeId: id,
        title:
          typeof view.title === 'string' && view.title.trim() ? view.title : id,
        componentType: view.componentType,
        initialProps: view.initialProps,
      });
    });
    return result;
  }, [currentView]);

  const onClickWorkspacePresetOption = async (
    presetId: string,
    workspaceAction: WorkspacePresetAction,
  ): Promise<void> => {
    // If affectedPreset is the FE defined `New workspace` - use the definition from FE instead of BE
    const affectedPreset =
      presetId === getEmptyMapWorkspace(t).id
        ? getEmptyMapWorkspace(t)
        : workspacePresets.find((preset) => preset.id === presetId);
    const presetTitle = affectedPreset?.title;
    switch (workspaceAction) {
      case WorkspacePresetAction.DUPLICATE: {
        const currentViews = currentViewsToBE();
        setWorkspaceActionDialogOptions({
          action: workspaceAction,
          presetId,
          formValues: {
            title: presetTitle!,
            abstract: affectedPreset!.abstract,
            description: affectedPreset!.description,
            scope,
            views: currentViews.length ? currentViews : [],
            mosaicNode: currentView.mosaicNode,
            viewType: currentView.viewType,
            syncGroups: currentView.syncGroups ?? [],
          },
        });
        break;
      }

      case WorkspacePresetAction.EDIT: {
        const adminMetadata =
          isAdmin && auth?.username
            ? {
                updated: new Date().toISOString(),
                updatedBy: auth.username,
              }
            : {};
        const buildFormValues = (
          presetDetails?: Partial<Omit<WorkspacePresetForBE, 'views'>>,
          presetViews: WorkspaceViewsFromBE[] = [],
        ): WorkspacePresetFormValues => ({
          title: presetDetails?.title ?? presetTitle ?? '',
          abstract: presetDetails?.abstract ?? affectedPreset?.abstract,
          description:
            presetDetails?.description ?? affectedPreset?.description,
          labels: presetDetails?.labels ?? affectedPreset?.labels,
          mosaicNode:
            presetDetails?.mosaicNode ?? currentView?.mosaicNode ?? undefined,
          scope: presetDetails?.scope ?? scope,
          views: presetViews,
          updated: new Date().toISOString(),
          ...adminMetadata,
        });

        // If editing the active workspace, keep using the current workspace data
        if (presetId === currentView?.id || !auth) {
          setWorkspaceActionDialogOptions({
            action: workspaceAction,
            presetId,
            formValues: buildFormValues(currentView, currentViewsToBE()),
          });
          break;
        }

        // Open dialog immediately with basic data, then hydrate with full preset once fetched
        setWorkspaceActionDialogOptions({
          action: workspaceAction,
          presetId,
          formValues: buildFormValues(),
        });

        void getWorkspace(presetId, auth)
          .then((presetFromBE) => {
            setWorkspaceActionDialogOptions({
              action: workspaceAction,
              presetId,
              formValues: buildFormValues(
                presetFromBE,
                presetFromBE.views ?? [],
              ),
            });
          })
          .catch((error: unknown) => {
            dispatch(
              workspaceListActions.onErrorWorkspacePresetAction({
                error: {
                  message: `${error}`,
                  type: WorkspaceListErrorType.VERBATIM_ERROR,
                  category: WorkspaceListErrorCategory.GENERIC,
                },
              }),
            );
          });
        break;
      }
      // case WorkspacePresetAction.SAVE: {
      // break;
      // }
      case WorkspacePresetAction.DELETE: {
        setWorkspaceActionDialogOptions({
          action: workspaceAction,
          presetId,
          formValues: { title: presetTitle, scope },
        });
        break;
      }
      case WorkspacePresetAction.DELETE_SYSTEM_PRESET: {
        setWorkspaceActionDialogOptions({
          action: workspaceAction,
          presetId,
          formValues: { title: presetTitle, scope },
        });
        break;
      }
      case WorkspacePresetAction.SHARE: {
        void getWorkspace(presetId, auth!).then(
          async (preset: WorkspacePresetForBE) =>
            updateWorkspace(
              presetId!,
              { ...preset, is_shared: !preset.is_shared },
              auth!,
            )
              .then(() => {
                dispatch(
                  workspaceListActions.onSuccessWorkspacePresetAction({
                    workspaceId: presetId,
                    action: WorkspacePresetAction.SHARE,
                    title: presetTitle!,
                    scope,
                    is_shared: preset.is_shared,
                  }),
                );
              })
              .catch((error: WorkspaceListErrorType) => {
                dispatch(
                  workspaceListActions.onErrorWorkspacePresetAction({
                    error: {
                      message: `${error}`,
                      type: WorkspaceListErrorType.VERBATIM_ERROR,
                      category: WorkspaceListErrorCategory.GENERIC,
                    },
                  }),
                );
              }),
        );
        break;
      }
      case WorkspacePresetAction.OPEN_TAB: {
        const url = getWorkspaceRouteUrl(presetId);
        window.open(url, '_blank');
        break;
      }

      case WorkspacePresetAction.COPY_TO_CLIPBOARD: {
        try {
          const preset = await getWorkspace(presetId, auth!);
          const expanded = await expandWorkspaceViewPresets(preset, auth!);
          const sanitized = sanitizeWorkspaceForClipboard(expanded);
          await writeToClipboard(JSON.stringify(sanitized, null, 2));
          dispatch(
            snackbarActions.openSnackbar({
              type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
              message: t('workspace-copy-success'),
            }),
          );
        } catch {
          dispatch(
            snackbarActions.openSnackbar({
              type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
              message: t('workspace-error-generic'),
            }),
          );
        }
        break;
      }

      default: {
        break;
      }
    }
  };

  const isAllSelected =
    workspaceListFilters.length > 0 &&
    workspaceListFilters.every((filter) => filter.isSelected);

  const shouldHideNewWorkspace =
    !isAllSelected || workspaceSearchQuery.length > 0 || hasActiveLabelFilters;

  const closeInfo = React.useCallback((): void => {
    setInfoPreset(undefined);
  }, []);

  const onOpenPresetInfo = React.useCallback(
    (preset: WorkspacePresetListItem): void => {
      setInfoPreset((current) =>
        current?.id === preset.id ? undefined : preset,
      );
    },
    [],
  );

  React.useEffect(() => {
    if (
      infoPreset &&
      !filteredPresets.some((preset) => preset.id === infoPreset.id)
    ) {
      closeInfo();
    }
  }, [infoPreset, filteredPresets, closeInfo]);

  return (
    <Box
      bgcolor="geowebColors.background.surface"
      data-testid="workspace-selectlist-connect"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      tabIndex={0}
    >
      <Box
        sx={{
          height: 4,
          overflow: 'hidden',
        }}
      >
        {!!isFetching && (
          <LinearProgress color="secondary" data-testid="loading-bar" />
        )}
      </Box>
      {!!error && (
        <div style={{ whiteSpace: 'normal' }}>
          <AlertBanner
            info={
              error.type === WorkspaceListErrorType.TRANSLATABLE_ERROR
                ? t(error.key, error.params)
                : error.message
            }
            shouldClose
            title={
              {
                GENERIC: '',
                AUTH: t('workspace-error-auth'),
              }[error.category] ?? t('workspace-error-fetch')
            }
            {...(error.category === WorkspaceListErrorCategory.GENERIC && {
              actionButtonProps: {
                title: t('workspace-retry'),
                onClick: (): void => {
                  void refreshWorkspacePresets();
                },
              },
            })}
          />
        </div>
      )}
      {!error && (
        <SearchField
          searchFilter={workspaceSearchQuery}
          setSearchFilter={searchFilter}
        />
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isLabelFilterOpen ? '280px 1fr' : '1fr',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {!!isLabelFilterOpen && (
          <Box
            sx={{
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'geowebColors.background.surface',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <FormProvider {...labelsFormMethods}>
              <WorkspaceMenuLabelsPanel
                menuLabels={labelFilters}
                onChange={onLabelsFilterChange}
              />
            </FormProvider>
          </Box>
        )}

        <Box
          ref={infoPanelAnchorRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: 1,
            overflow: 'hidden',
          }}
        >
          {!error && (
            <Box sx={{ marginBottom: 1, mx: 0.5 }}>
              <Box sx={{ display: 'flex', width: '100%' }}>
                <Filter
                  activeLabelFilterCount={activeLabelFilterCount}
                  filters={workspaceListFilters}
                  isAllSelected={isAllSelected}
                  isLabelFilterOpen={isLabelFilterOpen}
                  onClickFilterChip={onClickFilterChip}
                  onSearch={searchFilter}
                  onToggleLabelFilter={onToggleLabelFilter}
                  searchQuery={workspaceSearchQuery}
                  showLabelFilterToggle={showLabelFilterToggle}
                  showSearchField={false}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <LastUpdateTime
                  hideLabel
                  iconButtonSx={{ marginLeft: 0 }}
                  onPressRefresh={(): void => {
                    void refreshWorkspacePresets();
                  }}
                />
              </Box>
            </Box>
          )}

          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
            }}
          >
            <WorkspaceSelectList
              currentSelectedId={currentView.id || ''}
              hideNewWorkspace={shouldHideNewWorkspace}
              infoOpenPresetId={infoPreset?.id}
              isAdmin={isAdmin}
              isEditable={isLoggedIn}
              onClickWorkspacePreset={onClickWorkspacePreset}
              onClickWorkspacePresetOption={onClickWorkspacePresetOption}
              onOpenPresetInfo={onOpenPresetInfo}
              searchQuery={workspaceSearchQuery}
              workspacePresets={filteredPresets}
            />
            <WorkspacePresetInfo
              anchorEl={infoPanelAnchorRef.current}
              isOpen={Boolean(infoPreset)}
              onClose={closeInfo}
              preset={infoPreset}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
